import { z } from 'zod';

import { AlertsFiltersSchema } from '@/types/alerts-filters';

import { FeatureFlag, FeatureFlagSchema } from '../types/feature-flag';

import { CustomerTypeSchema } from './customer-type';
import { PowerUnitSchema } from './power-unit';

export const CustomerConfigSchema = z.object({
  id: z.string(),
  type: CustomerTypeSchema,
  topLevelIndex: z.number(),
  powerUnit: PowerUnitSchema,
  featureFlags: z.object({
    ai: FeatureFlagSchema.default(FeatureFlag.INTERNAL),
    alerts: FeatureFlagSchema.default(FeatureFlag.PUBLIC),
    alertNotifications: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    assets: FeatureFlagSchema.default(FeatureFlag.PUBLIC),
    capacityTest: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    communication: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    contracts: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    customDashboards: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    dataCompatibility: FeatureFlagSchema.default(FeatureFlag.PUBLIC),
    dataExplorer: FeatureFlagSchema.default(FeatureFlag.PUBLIC),
    details: FeatureFlagSchema.default(FeatureFlag.PUBLIC),
    detailsToDexLink: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    landingpage: FeatureFlagSchema.default(FeatureFlag.PUBLIC),
    modoEnergyIntegration: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    newDataExplorer: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    newNavigation: FeatureFlagSchema.default(FeatureFlag.PUBLIC),
    pcs: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    platformStatus: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    projectManagement: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    records: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    recordsCreate: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    recordsMetadata: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    siteview: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    siteviewConfigurator: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    sitemap: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    socOffsetViaVoltage: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    systemLogs: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    taskmanager: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    taskmanagerConfetti: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    trustCenter: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    userManagement: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    userPreferences: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    warranty: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    warrantyAlerts: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    warrantyV2: FeatureFlagSchema.default(FeatureFlag.DISABLED),
    warrantyV3: FeatureFlagSchema.default(FeatureFlag.DISABLED),
  }),
  defaultAlertsFilters: AlertsFiltersSchema.partial(),
  defaultDataExplorerFilters: z.object({
    timeRange: z.number(),
  }),
  sitesWithDifferentLevels: z.boolean(),
  top: z.object({
    labels: z.object({
      name: z.tuple([z.string(), z.string()]),
      id: z.string(),
      type: z.string(),
    }),
    defaultCustomerId: z.string().optional(),
  }),
  unit: z.object({
    labels: z.object({
      name: z.tuple([z.string(), z.string()]),
      id: z.string(),
      type: z.string(),
    }),
  }),
  pcs: z.object({
    labels: z.object({
      name: z.tuple([z.string(), z.string()]),
      id: z.string(),
      type: z.string(),
    }),
    defaultCustomerId: z.string().optional(),
  }),
  group: z.object({
    labels: z.object({
      name: z.tuple([z.string(), z.string()]),
      id: z.string(),
    }),
    defaultCustomerId: z.string().optional(),
  }),
  customDashboards: z.array(
    z.object({
      menuLabel: z.string(),
      pageTitle: z.string(),
      path: z.string(),
      dashboardId: z.string(),
    }),
  ),
  /** Asset IDs for which warranty penalties are not defined (show "not configured" per asset). */
  warrantyAssetIdsWithoutPenalties: z.array(z.string()).optional(),
  /** Asset IDs for which warranty conditions are not defined (show "not configured" per asset). */
  warrantyAssetIdsWithoutConditions: z.array(z.string()).optional(),
});

export type CustomerConfig = z.infer<typeof CustomerConfigSchema>;
